import LoginIcon from "@mui/icons-material/Login";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin } from "../api/auth";
import { ApiError } from "../api/client";
import { useSession } from "../auth/TokenContext";
import { brand } from "../theme";

export function LoginPage() {
  const navigate = useNavigate();
  const { setSession } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminLogin(email.trim().toLowerCase(), password);
      setSession({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        user: res.user,
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Email o contraseña incorrectos.");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Demasiados intentos. Esperá un minuto y volvé a probar.");
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "No pudimos conectar con el gateway. Revisá tu conexión.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${brand.navyDeep} 0%, ${brand.navy} 100%)`,
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 420,
          borderRadius: 3,
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Stack spacing={2.5}>
          <Box sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src="/brand/logo-on-light.png"
              alt="Novasis Pay"
              sx={{ height: 44, mb: 1 }}
            />
            <Typography variant="h6">Iniciar sesión</Typography>
            <Typography variant="caption" color="text.secondary">
              Panel administrativo de Novasis Pay.
            </Typography>
          </Box>

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            disabled={loading}
            required
          />

          <TextField
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
            required
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            disabled={loading || !email.trim() || !password}
          >
            {loading ? "Ingresando…" : "Ingresar"}
          </Button>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            ¿Olvidaste tu contraseña? Contactá al superadmin.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
